import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ParentConsentScreen } from "../../screens/parent/ParentConsentScreen";
import { ParentProgressScreen } from "../../screens/parent/ParentProgressScreen";
import { ParentProgressGate, type ChildIdentifier } from "../../screens/parent/ParentProgressGate";
import { ParentPayScreen } from "../../screens/parent/ParentPayScreen";
import { ParentPaidScreen } from "../../screens/parent/ParentPaidScreen";
import { ScreenState } from "../../components/ScreenState";
import { useAsync } from "../../lib/useAsync";
import {
  approveConsent,
  declineConsent,
  fetchConsent,
  fetchParentProgress,
  type ParentProgressView,
} from "../../api/parent";
import { fetchSharedCheckout, verifyCheckout } from "../../api/checkout";
import { openPaystackCheckout } from "../../lib/paystack";
import type { PaymentStep } from "../../components/PaymentBusy";
import { ApiError } from "../../api/client";

/**
 * Parent consent, on the real request.
 *
 * We identify the child by phone or email — whichever they registered with —
 * because onboarding does not collect a name. Fabricating one on a consent
 * screen would be a lie with legal consequences.
 */
export function ParentConsentRoute() {
  const { token = "" } = useParams();
  const [decided, setDecided] = useState<"approved" | "declined" | null>(null);
  const { data, loading, error, reload } = useAsync(() => fetchConsent(token), [token]);

  // Already masked by the server (+234 ••• ••• 4567) — enough to recognise.
  const childIdentifier = data ? (data.childPhone ?? data.childEmail ?? "your child") : "";

  return (
    <ScreenState loading={loading} error={error} onRetry={reload}>
      {data ? (
        <ParentConsentScreen
          childIdentifier={childIdentifier}
          state={
            decided === "approved" || data.status === "approved"
              ? "confirmed"
              : data.status === "expired"
                ? "expired"
                : "pending"
          }
          onApprove={() => void approveConsent(token).then(() => setDecided("approved"))}
          onDecline={() => void declineConsent(token).then(() => setDecided("declined"))}
        />
      ) : null}
    </ScreenState>
  );
}

/**
 * Progress, behind the gate.
 *
 * The link is one credential and the child's number is the second. A forwarded
 * or guessed link shows nothing on its own.
 */
export function ParentProgressRoute() {
  const { linkId = "" } = useParams();
  const [view, setView] = useState<ParentProgressView | null>(null);
  const [checking, setChecking] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const unlock = async (known: ChildIdentifier) => {
    setChecking(true);
    setFailure(null);

    try {
      setView(await fetchParentProgress(linkId, known));
    } catch (error) {
      setFailure(
        error instanceof ApiError
          ? error.message
          : "We couldn't check that. Try again in a moment.",
      );
    } finally {
      setChecking(false);
    }
  };

  if (!view) {
    return (
      <ParentProgressGate
        onSubmit={(known) => void unlock(known)}
        checking={checking}
        error={failure}
      />
    );
  }

  return (
    <ParentProgressScreen
      childName={view.childName}
      daysPractisedOfLastSeven={view.daysPractisedOfLastSeven}
      streakDays={view.streakDays}
      {...(view.lastMock ? { lastMock: view.lastMock } : {})}
      subjects={view.subjects}
    />
  );
}

/**
 * The parent payment page, on the real checkout the student shared.
 *
 * No auth and no account: the opaque reference in the URL is what identifies
 * the payment, exactly as the student's share link produced it.
 */
export function ParentPayRoute() {
  const navigate = useNavigate();
  const { reference = "" } = useParams();
  const { data, loading, error, reload } = useAsync(
    () => fetchSharedCheckout(reference),
    [reference],
  );

  /*
   * Where the parent ends up, rather than the student's success screen.
   *
   * They were being sent to "Keep practicing" — an invitation to sit an exam
   * they are not sitting, in an app they have no account for. A parent's
   * involvement ends at the receipt.
   */
  const [paid, setPaid] = useState<{ pending: boolean } | null>(null);
  const [step, setStep] = useState<PaymentStep>(null);

  /*
   * The parent pays through the same sheet, against the same reference.
   *
   * This button used to navigate straight to the pending screen — a parent
   * tapped "Pay" and was told to wait for a payment that had never been
   * attempted. The child would never be unlocked, and nobody would know why.
   */
  const pay = () => {
    if (!data || step) return;
    setStep("opening");

    try {
      openPaystackCheckout({
        reference: data.reference,
        email: data.email,
        amountMinor: data.amountMinor,
        onLoad: () => setStep(null),
        onSuccess: (charged) => {
          setStep("confirming");
          void verifyCheckout(charged)
            .then((result) => setPaid({ pending: !result.activated }))
            // The money may well have arrived and only our confirmation call
            // failed. Pending, never failed — a parent told their payment
            // failed pays twice.
            .catch(() => setPaid({ pending: true }))
            .finally(() => setStep(null));
        },
        // The sheet closes and the page stays as it was.
        onCancel: () => setStep(null),
      });
    } catch (caught) {
      setStep(null);
      void navigate("/payment/failed", {
        state: {
          reason: caught instanceof Error ? caught.message : "We couldn't start that payment.",
        },
      });
    }
  };

  if (paid) {
    return (
      <ParentPaidScreen
        childName={data?.childName ?? "your child"}
        pending={paid.pending}
        {...(data?.accessEndsAt ? { validUntil: monthAndYear(data.accessEndsAt) } : {})}
      />
    );
  }

  return (
    <ScreenState loading={loading} error={error} onRetry={reload}>
      {data ? (
        <ParentPayScreen
          childName={data.childName ?? "your child"}
          daysPractisedOfLastSeven={data.daysPractisedOfLastSeven ?? 0}
          onPay={pay}
          step={step}
        />
      ) : null}
    </ScreenState>
  );
}

/** "August 2027" — a parent wants the month, not a timestamp. */
function monthAndYear(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}
